import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, TextField, Button, Autocomplete, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { OverAllLoader } from "../../components/OverAllLoader";

// Upload API accepts a PDF (documents/upload, documents/update)
const ACCEPTED_TYPES = ["application/pdf"];
const MAX_FILE_MB = 5;
const MAX_PAGES = 20;

// Page count read from the PDF itself (no PDF library in the project): counts the page objects,
// falling back to the largest page-tree /Count. Returns null when it can't be read (e.g. compressed
// object streams) — the backend still enforces the limit in that case.
const countPdfPages = async (file) => {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let text = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
      text += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    const pageObjects = (text.match(/\/Type\s*\/Page(?![a-zA-Z])/g) || []).length;
    if (pageObjects) return pageObjects;
    const counts = [...text.matchAll(/\/Type\s*\/Pages[^>]*?\/Count\s+(\d+)/g)].map((m) => Number(m[1]));
    return counts.length ? Math.max(...counts) : null;
  } catch (error) {
    console.log("Error reading PDF page count:", error);
    return null;
  }
};

// Same field look as the Question Bank create page
const commonTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "#d1d1d1" },
    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
  },
};

export const programLabel = (option) => {
  if (!option) return "";
  const label = option.display_name || option.program_name || "";
  return option.program_code ? `[${option.program_code}] ${label}` : label;
};

export const subjectLabel = (option) => {
  if (!option) return "";
  const label = option.category_name || option.name || "";
  return option.category_code ? `[${option.category_code}] ${label}` : label;
};

export const subjectId = (option) => option?.category_id ?? option?.id;

// PDF link: base URL + file_path (or file_url), with single forward slashes
const pdfUrlOf = (document) => {
  const source = document?.file_path || document?.file_url;
  if (!source) return "";
  const filePath = source.replace(/\\/g, "/").replace(/\/{2,}/g, "/").replace(/^\/+/, "");
  return `${api.defaults.baseURL.replace(/\/+$/, "")}/${filePath}`;
};

// The PDF in the browser's PDF viewer, "Open in new tab" in the corner.
// Used by DocumentPreview and the Question Bank document dropdown (AddCategory).
export const PdfViewerDialog = ({ document, open, onClose }) => {
  const pdfUrl = pdfUrlOf(document);
  return (
    <Dialog open={open && !!pdfUrl} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 1.5, gap: 1 }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#1976d2", padding: "5px", display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <PictureAsPdfOutlinedIcon sx={{ color: "#e53935" }} />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {document?.document_name}
          </Box>
        </DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <IconButton onClick={() => window.open(pdfUrl, "_blank", "noopener")} title="Open in new tab">
            <OpenInNewIcon />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          component="iframe"
          src={pdfUrl}
          title={document?.document_name}
          sx={{ width: "100%", height: "78vh", border: "1px solid #e3eaf2", borderRadius: "10px" }}
        />
      </DialogContent>
    </Dialog>
  );
};

// Uploaded file card — click to open the PDF in the viewer.
// Used by the View page and by the Update page for the current file.
export const DocumentPreview = ({ document }) => {
  const [showPdf, setShowPdf] = useState(false);
  if (!pdfUrlOf(document)) return <Typography>-</Typography>;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setShowPdf(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowPdf(true);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          border: "1px solid #e1e7ef",
          borderRadius: "10px",
          backgroundColor: "#fafcff",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": { backgroundColor: "#f1f7ff", borderColor: "#1c97e3" },
        }}
      >
        <PictureAsPdfOutlinedIcon sx={{ color: "#e53935", fontSize: "28px" }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#26364a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {document.file_name || document.document_name}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#8a97a8", mt: 0.25 }}>
            {document.file_size_mb != null ? `${document.file_size_mb} MB · ` : ""}Click to view
          </Typography>
        </Box>
        <VisibilityIcon color="primary" />
      </Box>

      <PdfViewerDialog document={document} open={showPdf} onClose={() => setShowPdf(false)} />
    </>
  );
};

const AddDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Edit: the document details (documents/view) passed from the Documents list
  const editDocument = location.state?.document || null;
  const isEditMode = !!editDocument;

  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const schema = Yup.object().shape({
    program: Yup.object().nullable().required(`Please select a ${getLabel("PROGRAMS")}`),
    category: Yup.object().nullable().required(`Please select a ${getLabel("SUBJECT")}`),
    document_name: Yup.string()
      .trim()
      .required("Please enter the document name")
      .min(3, "Document name must be at least 3 characters")
      .max(100, "Document name cannot exceed 100 characters"),
    // A new document needs a file; when editing, the current file is kept unless a new one is picked
    document: isEditMode ? Yup.mixed().nullable() : Yup.mixed().nullable().required("Please upload a document"),
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      program: null,
      category: null,
      document_name: editDocument?.document_name || "",
      document: null,
    },
  });

  const selectedProgram = watch("program");
  const selectedFile = watch("document");

  // Same role-aware dropdown APIs as the Question Bank create page
  const fetchPrograms = async () => {
    try {
      const res = await api.post("GetAssignedProgramsDropdown", {});
      if (res?.data?.status === "success") setPrograms(res.data.data || []);
    } catch (error) {
      console.log("Error fetching programs:", error);
      showSnackbar("error", error?.response?.data?.message || `Failed to load ${getLabel("PROGRAMS")} list.`);
    }
  };

  const fetchCategoriesByProgram = async (programId) => {
    if (!programId) {
      setCategories([]);
      return [];
    }
    try {
      const res = await api.post("GetAssignedCategoriesDropdown", { program_id: programId });
      const list = res?.data?.status === "success" ? res.data.data || [] : [];
      setCategories(list);
      return list;
    } catch (error) {
      console.log("Error fetching categories by program:", error);
      showSnackbar("error", error?.response?.data?.message || `Failed to load ${getLabel("SUBJECT")} list.`);
      setCategories([]);
      return [];
    }
  };

  useEffect(() => {
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edit: pre-select the document's Program and Subject once the dropdowns load
  useEffect(() => {
    if (!isEditMode || !programs.length || selectedProgram) return;
    const program = programs.find((item) => item.program_id === editDocument.program_id);
    if (!program) return;
    setValue("program", program);
    fetchCategoriesByProgram(program.program_id).then((list) => {
      const category = list.find((item) => subjectId(item) === editDocument.category_id);
      if (category) setValue("category", category);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs]);

  // File checks shared by browse and drag & drop: PDF, max size, max pages
  const selectFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("document", { type: "manual", message: "Only PDF files are allowed" });
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError("document", { type: "manual", message: `File size cannot exceed ${MAX_FILE_MB} MB` });
      return;
    }
    const pages = await countPdfPages(file);
    if (pages !== null && pages > MAX_PAGES) {
      setError("document", { type: "manual", message: `PDF cannot exceed ${MAX_PAGES} pages (this file has ${pages})` });
      return;
    }
    clearErrors("document");
    setValue("document", file, { shouldValidate: true });
  };

  const removeFile = () => setValue("document", null, { shouldValidate: true });

  const handleClear = () => {
    reset({ program: null, category: null, document_name: "", document: null });
    setCategories([]);
  };

  const onSubmit = async (data) => {
    // documents/upload (create) and documents/update (edit) take multipart form data:
    // program_id, category_id, document_name, document (+ document_key on update).
    // The backend then processes the PDF in the background — its progress shows in the
    // Documents list Status column (PROCESSING → READY / FAILED; a failed one is re-uploaded from Edit).
    const formData = new FormData();
    formData.append("program_id", data.program.program_id);
    formData.append("category_id", subjectId(data.category));
    formData.append("document_name", data.document_name.trim());
    if (data.document) formData.append("document", data.document);
    if (isEditMode) formData.append("document_key", editDocument.document_key);

    try {
      setSubmitting(true);
      const res = await api.post(isEditMode ? "/documents/update" : "/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res?.data?.status !== "success") {
        showSnackbar("error", res?.data?.message || `Failed to ${isEditMode ? "update" : "upload"} the document.`);
        return;
      }
      showSnackbar("success", res.data.message || `Document ${isEditMode ? "updated" : "uploaded"} successfully.`);
      navigate("/documents");
    } catch (error) {
      console.log("Error saving document:", error);
      if (error?.response?.status !== 401) {
        const detail = error?.response?.data?.detail;
        showSnackbar(
          "error",
          error?.response?.data?.message ||
            (typeof detail === "string" && detail) ||
            `Failed to ${isEditMode ? "update" : "upload"} the document. Please try again.`
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {submitting && <OverAllLoader message={isEditMode ? "Updating document..." : "Uploading document..."} />}
      {/* Back */}
      <Grid
        sx={{ display: "flex", alignItems: "center", color: "#1c97e3", gap: "4px", cursor: "pointer", width: "fit-content", mb: 2 }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(-1);
          }
        }}
        onClick={() => navigate(-1)}
      >
        <ArrowBackIcon sx={{ fontSize: "17px" }} />
        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
      </Grid>

      {/* Header */}
      <Grid sx={{ pb: 2, display: "flex", gap: 1, alignItems: "center", borderBottom: "1px solid #d1d1d1" }}>
        <Grid sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px", display: "flex" }}>
          <FolderOpenOutlinedIcon sx={{ color: "white", fontSize: "35px" }} />
        </Grid>
        <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>{isEditMode ? "Update" : "Upload"} Document</Typography>
      </Grid>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 3, p: 3 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <DescriptionOutlinedIcon sx={{ color: "#1c97e3", fontSize: "20px" }} />
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c97e3" }}>Document Details</Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid #e3eaf2",
              borderRadius: "14px",
              p: 3,
              backgroundColor: "#fbfdff",
              display: "flex",
              flexDirection: "column",
              rowGap: 3,
            }}
          >
            {/* Program & Subject */}
            <Grid sx={{ display: "flex", gap: 5, flexDirection: { xs: "column", md: "row" } }}>
              <Grid sx={{ width: "100%" }}>
                <Typography>
                  {getLabel("PROGRAMS")} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="program"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={programs}
                      getOptionLabel={programLabel}
                      isOptionEqualToValue={(o, v) => o?.program_id === v?.program_id}
                      value={field.value}
                      onChange={(_, value) => {
                        field.onChange(value);
                        setValue("category", null);
                        fetchCategoriesByProgram(value?.program_id);
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.program_id}>
                          {programLabel(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`Select ${getLabel("PROGRAMS")}`}
                          error={!!errors.program}
                          helperText={errors.program?.message}
                          sx={commonTextFieldStyle}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid sx={{ width: "100%" }}>
                <Typography>
                  {getLabel("SUBJECT")} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={categories}
                      disabled={!selectedProgram}
                      getOptionLabel={subjectLabel}
                      isOptionEqualToValue={(o, v) => subjectId(o) === subjectId(v)}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      renderOption={(props, option) => (
                        <li {...props} key={subjectId(option)}>
                          {subjectLabel(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={selectedProgram ? `Select ${getLabel("SUBJECT")}` : `Select ${getLabel("PROGRAMS")} first`}
                          error={!!errors.category}
                          helperText={errors.category?.message}
                          sx={commonTextFieldStyle}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Document name */}
            <Grid sx={{ width: "100%" }}>
              <Typography>
                Document Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="document_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter document name"
                    error={!!errors.document_name}
                    helperText={errors.document_name?.message}
                    sx={commonTextFieldStyle}
                  />
                )}
              />
            </Grid>

            {/* Upload area — same look as the Templates upload box, plus drag & drop */}
            <Grid sx={{ width: "100%" }}>
              <Typography sx={{ mb: 1 }}>
                Upload Document {!isEditMode && <span style={{ color: "red" }}>*</span>}
              </Typography>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  selectFile(e.dataTransfer.files?.[0]);
                }}
                sx={{
                  minHeight: "140px",
                  borderRadius: "12px",
                  border: `1.5px dashed ${errors.document ? "#d32f2f" : isDragging ? "#1c97e3" : "#b8c4d6"}`,
                  backgroundColor: isDragging ? "#f1f7ff" : "#f8fbff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "#f1f7ff", borderColor: "#1c97e3" },
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: "32px", color: "#1c97e3" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#26364a" }}>
                  {isEditMode ? "Upload a new file to replace the current one" : "Upload Document"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#8a97a8" }}>
                  Drag and drop or click to browse · PDF only · Max {MAX_FILE_MB} MB · Max {MAX_PAGES} pages
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    selectFile(e.target.files?.[0]);
                    // Allows selecting the same file again
                    e.target.value = "";
                  }}
                />
              </Box>

              {/* Newly selected file (removable), else the current file when editing (click to view) */}
              {selectedFile ? (
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    border: "1px solid #e1e7ef",
                    borderRadius: "10px",
                    backgroundColor: "#fafcff",
                  }}
                >
                  <PictureAsPdfOutlinedIcon sx={{ color: "#e53935", fontSize: "24px" }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#26364a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#8a97a8", mt: 0.25 }}>
                      {`${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={removeFile} aria-label="Remove file">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                isEditMode && (
                  <Box sx={{ mt: 1.5 }}>
                    <DocumentPreview document={editDocument} />
                  </Box>
                )
              )}

              {errors.document && (
                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.75, ml: 1.5 }}>{errors.document.message}</Typography>
              )}
            </Grid>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              sx={{ textTransform: "none", borderRadius: "12px", px: 3, fontWeight: 600 }}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
            >
              {isEditMode ? "Update" : "Upload"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddDocument;
